import type { Brand, BrandDNA } from '@/entities/brand';
import type { Job } from '@/entities/job';
import type { Template } from '@/entities/template';
import { newId } from '@/shared/lib/id';
import { getDB } from '@/shared/storage';
import { estimateJobCost } from '@/shared/pricing';

export type JobSpec = Omit<Job, 'createdAt'> & { createdAt?: number };

export interface ResearchOnlyInput {
  brandName: string;
  brandUrl: string;
  scrapedSnippet: string;
  llmModelId: string;
  /** When set, the research job is attached to this brand. */
  brandId?: string;
  /** When set, the research job is added as a child of this composite. */
  parentJobId?: string;
  /** When true, the runner writes the resulting DNA back to the brand record. */
  applyToBrand?: boolean;
}

export interface SingleImageRetryInput {
  brand: Pick<Brand, 'id' | 'name'>;
  template: Template;
  dna: BrandDNA;
  llmModelId: string;
  imageModelId: string;
  imageResolutionPx: number;
  numImagesPerTemplate: number;
  /** Subset of brand product images to use as references; omitted = all. */
  productImageIds?: string[];
  /** Optional per-batch user direction; woven into the resolved prompt. */
  conceptBrief?: string;
}

const EMPTY_USAGE = {} as const;

/**
 * Single research job. Used during brand creation. No composite parent —
 * the research job is itself the "task" the user kicked off.
 */
export function planResearchOnly(input: ResearchOnlyInput): JobSpec[] {
  return [
    {
      id: newId('job'),
      kind: 'research',
      status: 'pending',
      ...(input.parentJobId && { parentJobId: input.parentJobId }),
      dependsOn: [],
      ...(input.brandId && { brandId: input.brandId }),
      label: `Research: ${input.brandName}`,
      input: {
        brandName: input.brandName,
        brandUrl: input.brandUrl,
        scrapedSnippet: input.scrapedSnippet,
        llmModelId: input.llmModelId,
        applyToBrand: input.applyToBrand ?? false,
      },
      usage: EMPTY_USAGE,
      actualCostUsd: 0,
    },
  ];
}

/**
 * One template's prompt-fill + image-generate, wrapped in a composite. Used
 * for "regenerate this template" affordances.
 */
export function planSingleTemplate(input: SingleImageRetryInput): JobSpec[] {
  const compositeId = newId('job');
  const promptId = newId('job');
  const n = Math.max(1, input.numImagesPerTemplate);
  const estimate = estimateJobCost({
    llmModelId: input.llmModelId,
    imageModelId: input.imageModelId,
    templateCount: 1,
    numImagesPerTemplate: n,
    llmCallsCount: 1,
  });
  const specs: JobSpec[] = [
    {
      id: compositeId,
      kind: 'composite',
      status: 'pending',
      dependsOn: [],
      brandId: input.brand.id,
      label:
        n > 1
          ? `Generate ${n} × ${input.template.name}`
          : `Generate: ${input.template.name}`,
      input: {},
      usage: EMPTY_USAGE,
      estimatedCostUsd: estimate,
      actualCostUsd: 0,
    },
    {
      id: promptId,
      kind: 'prompt-fill',
      status: 'pending',
      parentJobId: compositeId,
      dependsOn: [],
      brandId: input.brand.id,
      label: `Fill prompt: ${input.template.name}`,
      input: {
        brandId: input.brand.id,
        brandName: input.brand.name,
        template: input.template,
        llmModelId: input.llmModelId,
        // Each image-gen leaf is one prediction; resolved.numImages is always 1.
        numImages: 1,
        dna: input.dna,
        ...(input.conceptBrief && input.conceptBrief.trim().length > 0
          ? { conceptBrief: input.conceptBrief.trim() }
          : {}),
        ...(input.productImageIds && input.productImageIds.length > 0
          ? { productImageIds: input.productImageIds }
          : {}),
      },
      usage: EMPTY_USAGE,
      actualCostUsd: 0,
    },
  ];

  // Per-leaf seeds give each image-generate a distinct random seed so that
  // multiple visuals from the same template/prompt diverge at the diffusion
  // level. Seeds are persisted on the job so retries reproduce the same image.
  const seeds = new Set<number>();
  while (seeds.size < n) {
    seeds.add(Math.floor(Math.random() * 2_000_000_000));
  }
  const seedList = [...seeds];
  for (let i = 0; i < n; i++) {
    specs.push({
      id: newId('job'),
      kind: 'image-generate',
      status: 'pending',
      parentJobId: compositeId,
      dependsOn: [promptId],
      brandId: input.brand.id,
      label:
        n > 1
          ? `Generate image ${i + 1}/${n}: ${input.template.name}`
          : `Generate image: ${input.template.name}`,
      input: {
        brandId: input.brand.id,
        imageModelId: input.imageModelId,
        imageResolutionPx: input.imageResolutionPx,
        promptFillJobId: promptId,
        seed: seedList[i]!,
        ...(input.productImageIds && input.productImageIds.length > 0
          ? { productImageIds: input.productImageIds }
          : {}),
      },
      usage: EMPTY_USAGE,
      actualCostUsd: 0,
    });
  }
  return specs;
}

/** Persists the given specs in a single transaction and returns the root id(s). */
export async function createJobs(specs: readonly JobSpec[]): Promise<string[]> {
  const db = getDB();
  const now = Date.now();
  const records: Job[] = specs.map((s, i) => ({
    ...s,
    createdAt: s.createdAt ?? now + i,
  }));
  await db.transaction('rw', db.jobs, async () => {
    await db.jobs.bulkAdd(records);
  });
  return records.filter((r) => !r.parentJobId).map((r) => r.id);
}
